'use client';

import React, { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { getSpeedsizeImageUrl } from 'components/shared/custom-image/CustomImage';
import { geistMono } from 'utils/fonts';
import { FESTIVE_CDN_ASSETS } from '../festiveAssets';
import styles from './FestiveRisingCard.module.css';

// The site's cold-load splash (#loader, `splashFade` 800ms in styles/global.css, opaque
// until 60%) would hide the start of the entrance. Start it as the splash begins to fade;
// at once when there is no splash (client-side navigation). Same as AffiliateHero.
const SPLASH_FADE_START_MS = 480;

const useSplashFading = (): boolean => {
  const [fading, setFading] = useState(false);
  useEffect(() => {
    const loader = document.getElementById('loader');
    const splash = loader?.getAnimations?.().find((a) => a.playState !== 'finished');
    if (!splash) {
      setFading(true);
      return;
    }
    let raf = 0;
    const check = () => {
      const now = Number(splash.currentTime ?? 0);
      if (now >= SPLASH_FADE_START_MS || splash.playState === 'finished') {
        setFading(true);
        return;
      }
      raf = requestAnimationFrame(check);
    };
    check();
    return () => cancelAnimationFrame(raf);
  }, []);
  return fading;
};

/**
 * Figma's own render (progressive blur on the lower part). Sharp + blur must stay
 * separate — a single CDN blur plate breaks the desktop glare dissolve.
 */
const CARD_SRC = getSpeedsizeImageUrl(FESTIVE_CDN_ASSETS.giftCard);
/** Same card with no blur (corners cut to 24px radius + 2px AA margin). */
const CARD_SHARP_SRC = getSpeedsizeImageUrl(FESTIVE_CDN_ASSETS.giftCardSharp);
/** UH mark (Figma 5456:6816), 35.42×25.5. */
const UH_MARK_PATH =
  'M31.6636 0H3.75311V9.83133H9.35634L0 18.2289L6.87189 25.5L17.6555 15.9759L28.5448 25.5L35.4167 18.2289L26.0075 9.83133H31.6636V0Z';

/**
 * Lettering and mark engraved into the metal (after the user's etched-card reference): the
 * shapes are a recess — a touch darker than the surface — with a bright hairline where the
 * light from the top catches the lower bevel and a dark lip along the upper edge. Drawn
 * live so they stay razor-sharp; the card image itself is exported from Figma
 * with the lettering hidden.
 */
const Engraving: React.FC = () => {
  const t = useTranslations('performance-lab');
  return (
    <>
      {/* One deboss filter for lettering and mark: the SourceGraphic is the recess (a
          slightly darker tone), plus a soft shadow inside the top edge (the cut wall
          shading it), a faint glint inside the bottom edge, and a bright lip just outside
          the bottom edge where the light catches the bevel. */}
      <svg className={styles.engraveDefs} aria-hidden="true" focusable="false">
        <filter
          id="festive-deboss"
          x="-10%"
          y="-30%"
          width="120%"
          height="160%"
          colorInterpolationFilters="sRGB"
        >
          <feOffset in="SourceAlpha" dy="1.4" result="down" />
          <feComposite
            in="SourceAlpha"
            in2="down"
            operator="out"
            result="topBand"
          />
          <feGaussianBlur
            in="topBand"
            stdDeviation="0.6"
            result="topBandSoft"
          />
          <feFlood floodColor="#2e1f05" floodOpacity="0.7" />
          <feComposite in2="topBandSoft" operator="in" result="shade" />
          <feComposite
            in="shade"
            in2="SourceAlpha"
            operator="in"
            result="shadeIn"
          />
          <feOffset in="SourceAlpha" dy="-0.9" result="up" />
          <feComposite
            in="SourceAlpha"
            in2="up"
            operator="out"
            result="bottomBand"
          />
          <feFlood floodColor="#fff6dc" floodOpacity="0.38" />
          <feComposite in2="bottomBand" operator="in" result="glint" />
          <feOffset in="SourceAlpha" dy="1" result="down2" />
          <feComposite
            in="down2"
            in2="SourceAlpha"
            operator="out"
            result="lipBand"
          />
          <feGaussianBlur in="lipBand" stdDeviation="0.25" result="lipSoft" />
          <feFlood floodColor="#fffaf0" floodOpacity="0.8" />
          <feComposite in2="lipSoft" operator="in" result="lip" />
          <feMerge>
            <feMergeNode in="SourceGraphic" />
            <feMergeNode in="shadeIn" />
            <feMergeNode in="glint" />
            <feMergeNode in="lip" />
          </feMerge>
        </filter>
      </svg>
      <span className={styles.engraveText}>
        <span className={`${styles.engraveBrand} pl-dharma-title`}>
          {t('festive.versions.v2.giftCard.brand')}
        </span>
        <span className={styles.engraveEdition}>
          {t('festive.versions.v2.giftCard.edition')}
        </span>
      </span>
      <svg className={styles.engraveMark} viewBox="0 0 35.4167 25.5">
        <path d={UH_MARK_PATH} />
      </svg>
    </>
  );
};

/**
 * V3 hero visual (Figma 5456:6807), animated after the user's Robinhood card reference:
 * the card gets up from the back, pale, while a glare with gold trailing under it floods
 * its top edge and flows down it, as the card's lower part dissolves into Figma's
 * progressive blur; the haze then breathes slowly. Scrolling away drifts the card down
 * a touch.
 */
export const FestiveRisingCard: React.FC = () => {
  const play = useSplashFading();
  const stageClass = `${styles.stage} ${geistMono.variable}`;
  return (
    <div
      className={play ? `${stageClass} ${styles.play}` : stageClass}
      aria-hidden="true"
    >
      <div className={styles.rise}>
        <div className={styles.layers}>
          {/* Sharp card, shown above the haze's dissolve line. */}
          <img
            className={styles.sharp}
            src={CARD_SHARP_SRC}
            alt=""
            width={600}
            height={367}
            decoding="async"
            fetchPriority="high"
          />
          {/* Metal finish over the flat Figma fill: reflection, brushed micro-texture,
              bevelled edge (.metal). */}
          <span className={styles.metal}>
            <span className={styles.metalReflect} />
            <span className={styles.metalBrush} />
          </span>
          <Engraving />
          {/* Figma's blurred render: the card's lower part dissolves into it as the glare
              passes, then it breathes. */}
          <span className={styles.breathe}>
            <img
              className={styles.card}
              src={CARD_SRC}
              alt=""
              width={796}
              height={563}
              decoding="async"
              fetchPriority="high"
            />
          </span>
          {/* The glare, gold trailing under it, flowing down the card on entrance. */}
          <span className={styles.glare}>
            <span className={styles.flare} />
          </span>
        </div>
      </div>
    </div>
  );
};
